import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { StyledCarouselUl, StyledCarouselLi } from './style';
import {
  calculateCyclePage,
  pageTranslationWithOffset,
  pageTransition,
  isValidSlide
} from './paging';

class Carousel extends Component {
  constructor(props) {
    super(props);
    this.onPaginatorClick = this.onPaginatorClick.bind(this);
    this.onPreviousButtonClick = this.onPaginateButtonClick.bind(this, -1);
    this.onNextButtonClick = this.onPaginateButtonClick.bind(this, +1);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.state = {
      activePage: 0, // Store the index of the visible page
      previousActivePage: undefined
    };
    this.isActiveGesture = false;
  }

  componentDidMount() {
    this.startAutoPlay();
  }

  componentWillUnmount() {
    this.stopAutoPlay();
  }

  onPaginateButtonClick(pageDelta) {
    this.updateActivePage(pageDelta);
    this.resetAutoPlay();
  }

  onPaginatorClick(newActivePage) {
    const { activePage } = this.state;
    this.setState({
      activePage: newActivePage,
      previousActivePage: activePage
    });
    this.resetAutoPlay();
  }

  /**
   * Handle touchStart event for mobile Swipe or Pan gestures
   * @param {Event} event
   */
  onTouchStart(event) {
    const touchEvent = event.touches[0];
    this.stopAutoPlay(); // Stop autoplay if it is set
    this.isActiveGesture = true; // Active Gesture, It is used for remove transition effect
    this.startGestureX = touchEvent.pageX; // Set the start gesture X to calculate offsetPercentage
    this.startGestureTime = new Date().getTime(); // Set the start gesture Time to calculate if swipe event is valid
  }

  /**
   * With each touchMove event, calculate the percentage of offset to move the pages
   * @param {Event} event
   */
  onTouchMove(event) {
    const touchEvent = event.touches[0];
    const { clientWidth } = event.currentTarget;
    const offset = touchEvent.pageX - this.startGestureX;
    const offsetPercentage = (offset * 100) / clientWidth;
    // The max offset is -100 or 100 because you can only move one page for touchStart event
    const offsetGesturePercentage =
      offset < 0
        ? Math.max(offsetPercentage, -100)
        : Math.min(offsetPercentage, 100);

    this.setState({ offsetGesturePercentage });
  }

  /**
   * Handle touchEnd event:
   *  If the gesture is valid change the current active page for the new one.
   *  If the gesture is not valid restart the positions of the pages
   * @param {Event} event
   */
  onTouchEnd(event) {
    const totalPages = this.props.children.length;
    const { activePage: targetPage, offsetGesturePercentage } = this.state;
    const touchEvent = event.changedTouches[0];
    const pageDelta = offsetGesturePercentage < 0 ? 1 : -1;
    let activePage;
    let previousActivePage;

    this.isActiveGesture = false;

    if (
      isValidSlide(
        offsetGesturePercentage,
        this.startGestureX,
        touchEvent.pageX,
        this.startGestureTime
      )
    ) {
      // If the gesture is valid: replace activePage with the next/previous page
      activePage = calculateCyclePage(pageDelta, targetPage, totalPages);
      previousActivePage = targetPage;
    } else {
      // If the gesture is not valid, reset current pages to their start position with animation
      activePage = targetPage;
      previousActivePage = calculateCyclePage(
        pageDelta,
        targetPage,
        totalPages
      );
    }

    this.setState({
      activePage,
      previousActivePage,
      offsetGesturePercentage: 0
    });

    this.startAutoPlay(); // Start autoplay if carousel must have it
  }

  updateActivePage(pageDelta) {
    const totalPages = this.props.children.length;
    const { activePage } = this.state;

    this.setState({
      activePage: calculateCyclePage(pageDelta, activePage, totalPages),
      previousActivePage: activePage
    });
  }

  startAutoPlay() {
    const { autoPlayInterval } = this.props;
    if (autoPlayInterval) {
      this.autoplayInterval = setInterval(
        () => this.updateActivePage(1),
        autoPlayInterval * 1000
      );
    }
  }

  stopAutoPlay() {
    const { autoPlayInterval } = this.props;
    if (autoPlayInterval && this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
    }
  }

  resetAutoPlay() {
    this.stopAutoPlay();
    this.startAutoPlay();
  }

  render() {
    const {
      children,
      className,
      paginator: Paginator,
      previousButton: PreviousButton,
      nextButton: NextButton
    } = this.props;
    const {
      activePage,
      mouseEnter,
      previousActivePage,
      offsetGesturePercentage: offsetPercentage
    } = this.state;

    return (
      <StyledCarouselUl
        className={className}
        onTouchStart={this.onTouchStart}
        onTouchMove={this.onTouchMove}
        onTouchEnd={this.onTouchEnd}
      >
        <PreviousButton onClick={this.onPreviousButtonClick} />
        <NextButton onClick={this.onNextButtonClick} />
        {React.Children.map(children, (pageElement, page) => (
          <StyledCarouselLi
            key={`${page.toString()}${Math.round(Math.random() * 9999)}`}
            style={{
              transition: !this.isActiveGesture
                ? pageTransition(page, activePage, previousActivePage)
                : '',
              transform: pageTranslationWithOffset(
                page,
                activePage,
                children.length,
                offsetPercentage
              )
            }}
          >
            {pageElement}
          </StyledCarouselLi>
        ))}
        <Paginator
          activePage={activePage}
          pagesNumber={children.length}
          onClick={this.onPaginatorClick}
          mouseEnter={mouseEnter}
        />
      </StyledCarouselUl>
    );
  }
}

Carousel.propTypes = {
  autoPlayInterval: PropTypes.number, // Seconds
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
  className: PropTypes.string,
  paginator: PropTypes.func.isRequired,
  previousButton: PropTypes.func,
  nextButton: PropTypes.func
};

Carousel.defaultProps = {
  autoPlayInterval: undefined,
  previousButton: undefined,
  nextButton: undefined,
  className: undefined
};

export default Carousel;
